import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { projectsAPI } from '../services/api';
import { Plus, Edit, Trash2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { ImageUploader } from '../components/ImageUploader';

export const AdminProjects = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProject, setEditingProject] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: { de: '', en: '' },
    location: { de: '', en: '' },
    type: { de: '', en: '' },
    status: { de: '', en: '' },
    description: { de: '', en: '' },
    image: '',
    roi: ''
  });

  useEffect(() => {
    if (!user || !['admin', 'editor'].includes(user.role)) {
      navigate('/admin/login');
      return;
    }
    loadProjects();
  }, [user, navigate]);

  const loadProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      if (response.success) {
        setProjects(response.projects);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Projekte');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProject) {
        await projectsAPI.update(editingProject.id, formData);
        toast.success('Projekt erfolgreich aktualisiert');
      } else {
        await projectsAPI.create(formData);
        toast.success('Projekt erfolgreich erstellt');
      }
      setIsDialogOpen(false);
      resetForm();
      loadProjects();
    } catch (error) {
      toast.error(error.message || 'Fehler beim Speichern');
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title,
      location: project.location,
      type: project.type,
      status: project.status,
      description: project.description,
      image: project.image,
      roi: project.roi
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Projekt wirklich löschen?')) {
      try {
        await projectsAPI.delete(id);
        toast.success('Projekt gelöscht');
        loadProjects();
      } catch (error) {
        toast.error('Fehler beim Löschen');
      }
    }
  };

  const resetForm = () => {
    setEditingProject(null);
    setFormData({
      title: { de: '', en: '' },
      location: { de: '', en: '' },
      type: { de: '', en: '' },
      status: { de: '', en: '' },
      description: { de: '', en: '' },
      image: '',
      roi: ''
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-[#1a1a1a] to-black">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Button
              onClick={() => navigate('/admin/dashboard')}
              variant="ghost"
              className="text-[#C4A55A] hover:text-[#D4B56A] mb-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zurück zum Dashboard
            </Button>
            <h1 className="text-4xl font-bold text-white">Projekte verwalten</h1>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={resetForm}
                className="bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black"
              >
                <Plus className="h-4 w-4 mr-2" />
                Neues Projekt
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1a1a1a] border-[#C4A55A]/30 max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl text-white">
                  {editingProject ? 'Projekt bearbeiten' : 'Neues Projekt erstellen'}
                </DialogTitle>
                <DialogDescription className="text-gray-400">
                  Füllen Sie alle Felder in beiden Sprachen aus
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Titel (DE)</Label>
                    <Input
                      value={formData.title.de}
                      onChange={(e) => setFormData(prev => ({...prev, title: {...prev.title, de: e.target.value}}))}
                      className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Title (EN)</Label>
                    <Input
                      value={formData.title.en}
                      onChange={(e) => setFormData(prev => ({...prev, title: {...prev.title, en: e.target.value}}))}
                      className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Standort (DE)</Label>
                    <Input
                      value={formData.location.de}
                      onChange={(e) => setFormData(prev => ({...prev, location: {...prev.location, de: e.target.value}}))}
                      className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Location (EN)</Label>
                    <Input
                      value={formData.location.en}
                      onChange={(e) => setFormData(prev => ({...prev, location: {...prev.location, en: e.target.value}}))}
                      className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Typ (DE)</Label>
                    <Input
                      value={formData.type.de}
                      onChange={(e) => setFormData(prev => ({...prev, type: {...prev.type, de: e.target.value}}))}
                      className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Type (EN)</Label>
                    <Input
                      value={formData.type.en}
                      onChange={(e) => setFormData(prev => ({...prev, type: {...prev.type, en: e.target.value}}))}
                      className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Status (DE)</Label>
                    <Input
                      value={formData.status.de}
                      onChange={(e) => setFormData(prev => ({...prev, status: {...prev.status, de: e.target.value}}))}
                      className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Status (EN)</Label>
                    <Input
                      value={formData.status.en}
                      onChange={(e) => setFormData(prev => ({...prev, status: {...prev.status, en: e.target.value}}))}
                      className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <ImageUploader
                      label="Projekt-Bild"
                      value={formData.image}
                      onChange={(url) => setFormData(prev => ({ ...prev, image: url }))}
                      testId="project-image"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">ROI</Label>
                    <Input
                      value={formData.roi}
                      onChange={(e) => setFormData(prev => ({...prev, roi: e.target.value}))}
                      placeholder="z.B. 8.5%"
                      className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Beschreibung (DE)</Label>
                    <Textarea
                      value={formData.description.de}
                      onChange={(e) => setFormData(prev => ({...prev, description: {...prev.description, de: e.target.value}}))}
                      className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white resize-none"
                      rows={3}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Description (EN)</Label>
                    <Textarea
                      value={formData.description.en}
                      onChange={(e) => setFormData(prev => ({...prev, description: {...prev.description, en: e.target.value}}))}
                      className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white resize-none"
                      rows={3}
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold"
                >
                  {editingProject ? 'Projekt aktualisieren' : 'Projekt erstellen'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center text-gray-400">Lädt Projekte...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <Card key={project.id} className="bg-[#1a1a1a] border-[#C4A55A]/20 overflow-hidden">
                <div className="relative h-48">
                  <img src={project.image} alt={project.title.de} className="w-full h-full object-cover" />
                  <div className="absolute top-2 right-2 bg-[#C4A55A] text-black px-2 py-1 rounded text-sm font-semibold">
                    ROI {project.roi}
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-white">{project.title.de}</CardTitle>
                  <p className="text-gray-400 text-sm">{project.location.de}</p>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleEdit(project)}
                      variant="outline"
                      className="flex-1 border-[#C4A55A]/30 text-[#C4A55A] hover:bg-[#C4A55A]/10"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Bearbeiten
                    </Button>
                    <Button
                      onClick={() => handleDelete(project.id)}
                      variant="outline"
                      className="border-red-500/30 text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};